import BackendService from 'BackendService';
import FundOpportunityChatResponse from 'FundOpportunityChatResponse';
import { useCallback, useEffect, useRef } from 'react';
import AIChatMessage from '../../../models/AIChatMessage';
import { CurrentData } from './types';
import { appendChatResponseMessage, buildModelResponseText } from './utils';

interface UseFundOpportunityJobPollingOptions {
  setMessages: React.Dispatch<React.SetStateAction<AIChatMessage[]>>;
  setCurrentData: React.Dispatch<React.SetStateAction<CurrentData | undefined>>;
  setFieldBeingAskedFor: React.Dispatch<
    React.SetStateAction<keyof NonNullable<CurrentData> | null | undefined>
  >;
  setIsProcessing: React.Dispatch<React.SetStateAction<boolean>>;
  setHasUnprocessedDocuments: React.Dispatch<React.SetStateAction<boolean>>;
  setMissingRequiredFields: React.Dispatch<React.SetStateAction<string[]>>;
}

export function useFundOpportunityJobPolling({
  setMessages,
  setCurrentData,
  setFieldBeingAskedFor,
  setIsProcessing,
  setHasUnprocessedDocuments,
  setMissingRequiredFields,
}: UseFundOpportunityJobPollingOptions) {
  const jobPollingTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (jobPollingTimeoutRef.current !== null) {
        clearTimeout(jobPollingTimeoutRef.current);
        jobPollingTimeoutRef.current = null;
      }
    };
  }, []);

  const clearPollingTimeout = useCallback(() => {
    if (jobPollingTimeoutRef.current !== null) {
      clearTimeout(jobPollingTimeoutRef.current);
      jobPollingTimeoutRef.current = null;
    }
  }, []);

  const pollJobProgress = useCallback(
    (jobId: string, backendService: BackendService) => {
      const poll = async (): Promise<void> => {
        try {
          const job = await backendService.getJobProgress(jobId);

          const progressMessage: AIChatMessage = {
            role: 'model',
            parts: [{ text: job.progressText || 'Processing documents...' }],
            timestamp: new Date(),
          };

          setMessages((previousMessages) => {
            const updatedMessages = [...previousMessages];
            if (
              updatedMessages.length > 0 &&
              updatedMessages[updatedMessages.length - 1].role === 'model'
            ) {
              updatedMessages.pop();
            }
            updatedMessages.push(progressMessage);
            return updatedMessages;
          });

          if (job.status === 'COMPLETED') {
            clearPollingTimeout();

            const metadata = (job as { metadata?: { result?: FundOpportunityChatResponse } })
              .metadata;
            const result = metadata?.result;

            if (result) {
              const { currentData, furtherRequestForDetail } = result;
              const question = furtherRequestForDetail?.question || '';
              const response = furtherRequestForDetail?.responseToUserQuestion || '';
              const nextFieldBeingAskedFor = furtherRequestForDetail?.fieldBeingAskedFor || null;

              setMessages((previousMessages) => {
                const updatedMessages = [...previousMessages];
                if (
                  updatedMessages.length > 0 &&
                  updatedMessages[updatedMessages.length - 1].role === 'model'
                ) {
                  updatedMessages.pop();
                }

                if (question || response) {
                  updatedMessages.push({
                    role: 'model',
                    parts: [{ text: buildModelResponseText(response, question) }],
                    timestamp: new Date(),
                  });
                }

                return updatedMessages;
              });

              setCurrentData(currentData);
              setFieldBeingAskedFor(nextFieldBeingAskedFor);
              setMissingRequiredFields(result.missingRequiredFields || []);
              setIsProcessing(false);
              setHasUnprocessedDocuments(false);
            } else {
              setMessages((previousMessages) => [
                ...previousMessages,
                {
                  role: 'model',
                  parts: [{ text: '✅ Document processing completed successfully.' }],
                  timestamp: new Date(),
                },
              ]);
              setIsProcessing(false);
              setHasUnprocessedDocuments(false);
            }
          } else if (job.status === 'FAILED') {
            clearPollingTimeout();

            setMessages((previousMessages) => [
              ...previousMessages,
              {
                role: 'model',
                parts: [
                  {
                    text: job.progressText || 'Document processing failed. Please try again.',
                  },
                ],
                timestamp: new Date(),
              },
            ]);
            setIsProcessing(false);
          } else {
            const interval = job.pollingSuggestions?.suggestedInterval || 2000;
            jobPollingTimeoutRef.current = window.setTimeout(poll, interval);
          }
        } catch (error) {
          console.error('Error polling job progress:', error);
          clearPollingTimeout();

          setMessages((previousMessages) => [
            ...previousMessages,
            {
              role: 'model',
              parts: [{ text: 'Error checking processing status. Please try again.' }],
              timestamp: new Date(),
            },
          ]);
          setIsProcessing(false);
        }
      };

      poll();
    },
    [
      clearPollingTimeout,
      setCurrentData,
      setFieldBeingAskedFor,
      setHasUnprocessedDocuments,
      setIsProcessing,
      setMessages,
      setMissingRequiredFields,
    ],
  );

  return { pollJobProgress };
}
